import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Per-group assignment block: choose a scope (Everyone / Edition / Customer) and, unlike
 * the old bottom-of-page form, LOAD the saved assignment for that scope so the allowed
 * choices + visibility reflect reality. Saves via `adminSetOptionAssignment`. Precedence
 * (customer > edition > global) is resolved server-side; this just authors one scope row.
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import type { CustomerRecord, OptionGroup } from "@genz/platform";

type ScopeType = "global" | "edition" | "customer";
const GLOBAL_SCOPE_KEY = "*";

/** The saved row for the active scope, or `null` when no row exists for it yet. */
type SavedRow = { allowed: string[]; visible: boolean };

export function GroupAssignment({ group }: { group: OptionGroup }) {
  const { client } = useServices();
  const [scopeType, setScopeType] = useState<ScopeType>("edition");
  const [edition, setEdition] = useState<"brand" | "studio">("brand");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState<CustomerRecord[] | null>(null);
  const [visible, setVisible] = useState(true);
  const [allowed, setAllowed] = useState<string[]>([]);
  // What the server currently has for the active scope (null = no row at all). The
  // editor's own defaults are NOT a saved state — see `dirty` below.
  const [savedRow, setSavedRow] = useState<SavedRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tid = `option-group-${group.id}-assignment`;

  const scopeKey =
    scopeType === "global" ? GLOBAL_SCOPE_KEY : scopeType === "edition" ? edition : customerId.trim();

  // Load the saved assignment for the active scope (the fix for "always starts blank").
  const load = useCallback(async () => {
    setSaved(false);
    if (!scopeKey) {
      setAllowed([]);
      setVisible(true);
      setSavedRow(null);
      return;
    }
    try {
      const { assignments } = await client.adminGetOptionAssignments(scopeType, scopeKey);
      const a = assignments.find((x) => x.group_id === group.id);
      setAllowed(a?.allowed_choice_ids ?? []);
      setVisible(a ? a.visible : true);
      setSavedRow(a ? { allowed: a.allowed_choice_ids ?? [], visible: a.visible } : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client, scopeType, scopeKey, group.id]);

  useEffect(() => { void load(); }, [load]);

  // Lazy customer list the first time the Customer scope is chosen.
  useEffect(() => {
    if (scopeType !== "customer" || customers !== null) return;
    let live = true;
    void (async () => {
      try {
        const rows = await client.listCustomers();
        if (live) setCustomers(rows);
      } catch {
        if (live) setCustomers([]);
      }
    })();
    return () => { live = false; };
  }, [scopeType, customers, client]);

  const toggle = (id: string, on: boolean) => {
    setAllowed((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
    setSaved(false);
  };

  /**
   * P0-3: this editor used to PUT whatever it happened to be showing. Opening a group
   * (which defaults to scope=edition/brand) and pressing Save wrote an override with an
   * EMPTY allowed-list, and the server drops a group whose effective assignment allows
   * nothing — so a look-don't-touch visit hid the group for that whole edition.
   * Two guards: (1) only PUT when the operator actually changed something against the
   * SAVED row, (2) an empty allowed-list is never a silent "hide everything" — it must
   * be confirmed. `summary` states, before the click, exactly what Save will do.
   */
  const baseline: SavedRow = savedRow ?? { allowed: [], visible: true };
  const dirty =
    visible !== baseline.visible ||
    allowed.length !== baseline.allowed.length ||
    allowed.some((id) => !baseline.allowed.includes(id));

  const scopeLabel =
    scopeType === "global" ? "everyone" : scopeType === "edition" ? `the ${edition} edition` : "this customer";
  const summary = !scopeKey
    ? "Pick a customer to load their assignment."
    : !dirty
      ? savedRow
        ? `No changes. “Re-save” rewrites the same values; “Remove override” drops it so ${scopeLabel} falls back to the broader scope.`
        : `No saved assignment for ${scopeLabel} — nothing will be written.`
      : allowed.length === 0
        ? `Save will HIDE “${group.label}” for ${scopeLabel} (no choices allowed).`
        : `Save will allow ${allowed.length} of ${group.choices.length} choices for ${scopeLabel}${visible ? "" : " (hidden)"}.`;

  async function save() {
    if (!scopeKey) {
      setError("Pick a customer first.");
      return;
    }
    // A no-change Save is allowed when a row EXISTS: it rewrites the same values, which is the
    // operator's only recovery when they suspect a save did not land. Refusing it (the button
    // used to be disabled) left them with no way to retry. With NO row, a no-change save would
    // instead CREATE an empty override that shadows the broader scope — that stays refused.
    if (!dirty && !savedRow) {
      setError("Nothing to save — there is no saved assignment here, and saving an empty one " +
        "would create an override that hides this group.");
      return;
    }
    if (
      allowed.length === 0 &&
      !await confirmAction(
        `No choices are ticked. Saving will hide “${group.label}” from ${scopeLabel} entirely` +
          `${savedRow ? ", replacing the saved assignment" : ", creating an override that also shadows any broader scope"}.` +
          " Continue?",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await client.adminSetOptionAssignment({
        scope_type: scopeType,
        scope_key: scopeKey,
        group_id: group.id,
        allowed_choice_ids: allowed,
        visible,
      });
      setSavedRow({ allowed: [...allowed], visible });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Delete the override at this scope so the group falls back to the broader one
   * (customer > edition > global). Ticking every choice is NOT equivalent: the narrow row
   * still shadows the broader default, which is why "put it back how it was" was previously
   * impossible from this editor.
   */
  async function removeOverride() {
    if (!scopeKey || !savedRow) return;
    if (!await confirmAction(
      `Remove the saved assignment for ${scopeLabel}? “${group.label}” will fall back to ` +
        `${scopeType === "customer" ? "the edition (then the global) default" : scopeType === "edition" ? "the global default" : "no assignment at all"}.`,
    )) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await client.adminDeleteOptionAssignment(scopeType, scopeKey, group.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="options-assignment" data-testid={tid}>
      <span className="faint">Who gets this group?</span>
      <div className="options-form-row">
        <label className="field"><span>Scope</span>
          <select className="input" value={scopeType} data-testid={`${tid}-scope-type`}
            onChange={(e) => setScopeType(e.target.value as ScopeType)}>
            <option value="global">Everyone</option>
            <option value="edition">Edition</option>
            <option value="customer">Customer</option>
          </select></label>

        {scopeType === "edition" && (
          <label className="field"><span>Edition</span>
            <select className="input" value={edition} data-testid={`${tid}-edition`}
              onChange={(e) => setEdition(e.target.value as "brand" | "studio")}>
              <option value="brand">brand</option>
              <option value="studio">studio</option>
            </select></label>
        )}
        {scopeType === "customer" && (customers && customers.length ? (
          <label className="field"><span>Customer</span>
            <select className="input" value={customerId} data-testid={`${tid}-customer`}
              onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">— pick a customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.email} ({c.edition})</option>)}
            </select></label>
        ) : scopeType === "customer" ? (
          <label className="field"><span>Customer id</span>
            <input className="input" type="text" value={customerId} data-testid={`${tid}-customer`}
              onChange={(e) => setCustomerId(e.target.value)} /></label>
        ) : null)}

        <label className="field field--check">
          <input type="checkbox" checked={visible} data-testid={`${tid}-visible`}
            onChange={(e) => { setVisible(e.target.checked); setSaved(false); }} /><span>visible</span></label>
      </div>

      <div className="options-allowed" data-testid={`${tid}-allowed`}>
        <span className="faint">Allowed choices</span>
        {group.choices.length ? group.choices.map((c) => (
          <label className="options-allowed__item" key={c.id}>
            <input type="checkbox" checked={allowed.includes(c.id)}
              data-testid={`${tid}-allowed-${c.id}`} onChange={(e) => toggle(c.id, e.target.checked)} />
            <span>{c.label} <span className="faint">({c.value})</span></span>
          </label>
        )) : <span className="faint">No choices yet.</span>}
      </div>

      {error && <p role="alert" data-testid={`${tid}-error`}><ErrorNotice detail={error} /></p>}
      <p className="faint" data-testid={`${tid}-summary`}>{summary}</p>
      <div className="options-form-row">
        {/* WS-Z5 rule 7: the per-card scope override is a sub-form inside an expanded
            group, not the Options panel's one invitation. */}
        <button type="button" className="btn btn--sm"
          disabled={busy || !scopeKey || (!dirty && !savedRow)}
          data-testid={`${tid}-save`} onClick={() => void save()}>
          {busy ? "Saving…" : dirty ? "Save" : "Re-save"}
        </button>
        {savedRow && (
          <button type="button" className="btn btn--sm btn--ghost" disabled={busy}
            data-testid={`${tid}-remove`} onClick={() => void removeOverride()}>
            Remove override
          </button>
        )}
        {saved && <span className="faint" data-testid={`${tid}-saved`}>Saved.</span>}
      </div>
    </div>
  );
}
